/**
 * `@cogo/acceptance-contracts` — BROWSER-SAFE barrel.
 *
 * Everything exported here must be reachable from a browser bundle: types, and
 * values that neither touch the filesystem nor mint a content hash. Studio's
 * UI imports this barrel directly.
 *
 * The Node-only half — the filesystem-backed evidence stores, and the
 * receipt/attestation MINTING functions that hash content through
 * `node:crypto` — lives behind `@cogo/acceptance-contracts/node`. Types for
 * those receipts stay here, because reading and describing a receipt is
 * browser work; producing one is not.
 *
 * Do not add a value export here that reaches a `node:` builtin. The gate is
 * `apps/cogo-studio/src/__tests__/browser-graph-node-builtins.test.ts`, which
 * walks Studio's real vite entry and fails on any reachable builtin — add one
 * and it goes red, naming the chain.
 */
export type * from "./application-series-binding";
export type * from "./application-attempt-binding";
export * from "./application-stage-binding";
export type * from "./application-evidence-binding";
export * from "./application-prerequisite-scope";
export * from "./application-prerequisite-json";
// Values mint/verify content hashes -> `@cogo/acceptance-contracts/node`.
export type * from "./prerequisite-producer-attestation";
export type * from "./application-prerequisite-observation-receipt";
export type * from "./application-kg-preflight";
export type * from "./application-kg-preflight-observation";
export type * from "./producer-attestation";
export type * from "./external-blocker-observation";
// Values mint/verify content hashes -> `@cogo/acceptance-contracts/node`.
export type * from "./application-observation-receipt";
export type * from "./application-generation-admission";
export type * from "./application-generation-eligibility-issuance";
export type * from "./application-artifact-persistence-admission";
export type * from "./application-artifact-persistence-eligibility-issuance";
export type * from "./observation-store";
export type * from "./receipt-store";
// Filesystem-backed stores (`node-*-store`) -> `@cogo/acceptance-contracts/node`.
export { APPLICATION_AUTHORIZATION_CONSUMPTION_KEYS, applicationAuthorizationConsumptionRecordRef } from "./authorization-consumption";
export type * from "./authorization-consumption";
export { APPLICATION_PUBLISHED_READBACK_FETCH_POLICY, APPLICATION_PUBLISHED_READBACK_OBSERVATION_KEYS } from "./application-published-readback-observation";
export type * from "./application-published-readback-observation";
export { APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS, parseApplicationOntologyPublicationRefs } from "./application-ontology-publication";
export type * from "./application-ontology-publication";

import type { ApplicationArtifactPersistenceAdmissionDecision, ApplicationArtifactPersistenceAdmissionRequest } from "./application-artifact-persistence-admission";
import type { ApplicationArtifactPersistenceEligibilityIssuanceDecision, ApplicationArtifactPersistenceEligibilityIssuanceRequest } from "./application-artifact-persistence-eligibility-issuance";
import type { ApplicationGenerationAdmissionDecision, ApplicationGenerationAdmissionRequest } from "./application-generation-admission";
import type { ApplicationGenerationEligibilityIssuanceDecision, ApplicationGenerationEligibilityIssuanceRequest } from "./application-generation-eligibility-issuance";
import type { ApplicationKgPreflightDecision, ApplicationKgPreflightRequest } from "./application-kg-preflight";

export interface PlatformApplicationEvidenceAdmissionClient {
  runKgPreflight(input: ApplicationKgPreflightRequest): Promise<ApplicationKgPreflightDecision>;
  issueGenerationEligibility(input: ApplicationGenerationEligibilityIssuanceRequest): Promise<ApplicationGenerationEligibilityIssuanceDecision>;
  /** Project-scoped by PATH: `projectId` is not a body field, see the request contract. */
  issueArtifactPersistenceEligibility(input: ApplicationArtifactPersistenceEligibilityIssuanceRequest, projectId: string): Promise<ApplicationArtifactPersistenceEligibilityIssuanceDecision>;
  verifyGenerationEligibility(input: ApplicationGenerationAdmissionRequest): Promise<ApplicationGenerationAdmissionDecision>;
  verifyArtifactPersistenceEligibility(input: ApplicationArtifactPersistenceAdmissionRequest): Promise<ApplicationArtifactPersistenceAdmissionDecision>;
}
